import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, DollarSign, RotateCcw, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CUR = { usd: "$", eur: "€", gbp: "£", mxn: "$" };
function money(amt, cur) {
  return `${CUR[cur] || ""}${Number(amt || 0).toFixed(2)} ${(cur || "usd").toUpperCase()}`;
}
function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Transactions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);

  const load = () => api.get("/admin/transactions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const refund = async (id) => {
    if (!window.confirm("Mark this transaction as refunded? It will be removed from your revenue total.")) return;
    try {
      await api.post(`/admin/transactions/${id}/refund`);
      toast.success("Marked as refunded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const total = (rows || []).filter((r) => !r.refunded).reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="p-8" data-testid="transactions-page">
      <Link to="/admin" className="text-sm text-[#6D28B5] font-semibold flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Sales &amp; transactions</h1>
          <p className="text-[#4B5563] mt-1">{isAdmin ? "Every paid order across all hosts and experiences." : "Every paid order for your experiences."}</p>
        </div>
        <div className="bg-white border border-[#0A0A0A] px-6 py-4">
          <div className="text-xs uppercase tracking-wider font-bold text-[#4B5563] flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Net revenue</div>
          <div className="text-3xl font-black tracking-tighter" data-testid="transactions-total">${total.toFixed(2)}</div>
        </div>
      </div>

      {rows === null ? (
        <div className="text-[#9CA3AF] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#9CA3AF] p-12 text-center text-[#6B7280]" data-testid="transactions-empty">No sales yet. Paid registrations will show up here.</div>
      ) : (
        <div className="bg-white border border-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#0A0A0A] text-left text-xs uppercase tracking-wider text-[#4B5563]">
                <th className="px-4 py-3 font-bold">Date</th>
                <th className="px-4 py-3 font-bold">Experience</th>
                <th className="px-4 py-3 font-bold">Attendee</th>
                {isAdmin && <th className="px-4 py-3 font-bold">Host</th>}
                <th className="px-4 py-3 font-bold">Amount</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-[#E5E7EB] last:border-0" data-testid={`txn-row-${t.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-[#4B5563]">{fmt(t.created_at)}</td>
                  <td className="px-4 py-3 font-semibold">{t.experience_title}</td>
                  <td className="px-4 py-3">
                    <div>{t.attendee_name || "—"}</div>
                    <div className="text-xs text-[#9CA3AF]">{t.attendee_email}</div>
                  </td>
                  {isAdmin && <td className="px-4 py-3 text-[#4B5563]">{t.host_name || "—"}</td>}
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{money(t.amount, t.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 ${t.refunded ? "bg-[#FEE2E2] text-[#B91C1C]" : t.status === "paid" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                      {t.refunded ? "Refunded" : t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!t.refunded && t.status === "paid" && (
                      <button onClick={() => refund(t.id)} data-testid={`refund-btn-${t.id}`} className="text-[#FF3B30] hover:bg-red-50 px-2 py-1 text-xs font-semibold inline-flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
